import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { fetchProfileData } from "../utils/profile";
import appConfig, { getPortalKey } from "../config";
import terologo from "../assets/icons/leechlake.png";
import critLogo from "../assets/icons/critLogo.png";
import suBlackLogo from "../assets/icons/su.png";
import { User, Edit, Settings, FileText, HelpCircle, Loader } from "lucide-react";
import { PBPUTILS } from "../config/API";
import ccthImage1 from "../assets/icons/ccth/1.jpg";
import ccthImage2 from "../assets/icons/ccth/2.jpg";
import ccthImage3 from "../assets/icons/ccth/3.jpg";
import ccthImage4 from "../assets/icons/ccth/4.jpg";
import ccthImage5 from "../assets/icons/ccth/5.jpg";

const Home = () => {
  const config = useMemo(() => {
    const key = getPortalKey();
    return appConfig[key] || appConfig.portal;
  }, []);

  const [appName, setAppName] = useState(config.name);
  const [appLogo, setAppLogo] = useState(config.logo);

  const navigate = useNavigate();
  const [ProgramTitle, setProgramTitle] = useState("both");
  const [Line1, setLine1] = useState("");
  const [Line2, setLine2] = useState("");
  const [userType, setUserType] = useState("");
  const [portalSegment] = useState(() => {
    const pathSegments = window.location.pathname.split("/");
    return pathSegments.filter((segment) => segment)[0];
  });

  // Business selection modal
  const [showBusinessSelector, setShowBusinessSelector] = useState(false);
  const [businessList, setBusinessList] = useState([]); // Array of businesses
  const [selectedBusinessIndex, setSelectedBusinessIndex] = useState(0);
  const [isLoadingTaxPayerInfo, setIsLoadingTaxPayerInfo] = useState(false);

  const handleLogout = () => {
    sessionStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("authToken");
    navigate("/");
  };

  // New function to fetch taxpayer information
  const fetchTaxPayerInfo = async (taxpayerId) => {
    try {
      const response = await fetch(
        PBPUTILS + `?taxpayer=${taxpayerId}&action=GetTaxPayerInfo`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching taxpayer information:", error);
      throw error;
    }
  };

  // Helper function to get taxpayer ID with fallback
  const getTaxpayerId = (business) => {
    // First try "Taxpayer" key
    if (business["Taxpayer"] && business["Taxpayer"].trim() !== "") {
      return business["Taxpayer"];
    }
    // Fallback to "Tax Payer" key if "Taxpayer" is empty
    if (business["Tax Payer"] && business["Tax Payer"].trim() !== "") {
      return business["Tax Payer"];
    }
    // Return empty string if neither exists or both are empty
    return "";
  };

  useEffect(() => {
    // Get login options from portal settings
    const settings = JSON.parse(sessionStorage.getItem("portalSettings"));
    const options = settings?.ProgramTitle || "";
    const ln1 = settings?.[userType + "Line1"] || "";
    const ln2 = settings?.[userType + "Line2"] || "";

    setLine1(ln1);
    setLine2(ln2);
    setProgramTitle(options);
  }, [userType]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await fetchProfileData();
       
        if (!config.name === "Prairie Band Potawatomi Nation") {
          window.dispatchEvent(new Event('localStorageChange'));
        }

        if (data.status === "error" && data.message === "not logged in") {
          handleLogout();
          return;
        }
        setUserType(data["Account Type"]);
        // Check if need business selection
        if (Array.isArray(data)) {
          if (!localStorage.getItem("selectedTaxPayer") && data.length > 0) {
            setBusinessList(data);
            setShowBusinessSelector(true);
          }
        } else {
          localStorage.removeItem("selectedTaxPayer");
          localStorage.removeItem("selectedBusinessName");
          localStorage.removeItem("selectedTaxPayerType");
          localStorage.removeItem("businessLicenseExpirationDate");
          localStorage.removeItem("businessLicenseStatus");
          localStorage.removeItem("businessAddress"); // Clear address as well
        }
      } catch (error) {
        // handleLogout();
      }
    };

    loadProfile();
  }, []);

  // Handle dropdown change inside selector view
  const handleBusinessDropdownChange = (e) => {
    setSelectedBusinessIndex(Number(e.target.value));
  };

  // Save selection and close selector
  const handleBusinessSelect = async () => {
    const selectedBusiness = businessList[selectedBusinessIndex];
    const taxpayerId = getTaxpayerId(selectedBusiness);

    if (!taxpayerId) {
      console.error("No taxpayer ID found for selected business");
      setIsLoadingTaxPayerInfo(false);
      return;
    }

    const businessName = selectedBusiness["Business Name"] || "";
    const taxPayerType = selectedBusiness["Tax Payer Type"];

    // Save basic business info to localStorage
    localStorage.setItem("selectedTaxPayer", taxpayerId);
    localStorage.setItem("selectedBusinessName", businessName);
    localStorage.setItem("selectedBusinessAddress", selectedBusiness["Business Address "] || selectedBusiness["Business Address"] || selectedBusiness["Address"] || "");
    localStorage.setItem("selectedBusinessCity", selectedBusiness["City"] || selectedBusiness["Business City"] || "");
    localStorage.setItem("selectedBusinessState", selectedBusiness["State"] || selectedBusiness["Business State"] || "");
    localStorage.setItem("selectedBusinessZip", selectedBusiness["Zip Code"] || selectedBusiness["Business Zip"] || "");
    localStorage.setItem("selectedTaxPayerType", taxPayerType);
    console.log(`Selected business: ${businessName} (Taxpayer ID: ${taxpayerId})`);

    // Save Tribal license number if available
    if (selectedBusiness["Business License"] && Array.isArray(selectedBusiness["Business License"]) && selectedBusiness["Business License"].length > 0) {
      const tribalLicenseNumber = selectedBusiness["Business License"][0]["Tribal license number"];
      if (tribalLicenseNumber) {
        localStorage.setItem("tribalLicenseNumber", tribalLicenseNumber);
        console.log("Tribal license number saved:", tribalLicenseNumber);
      } else {
        localStorage.removeItem("tribalLicenseNumber");
      }
    } else {
      localStorage.removeItem("tribalLicenseNumber");
    }

    setIsLoadingTaxPayerInfo(true);
    
    try {
      // Fetch additional taxpayer information including license expiration date and address
      const taxpayerInfo = await fetchTaxPayerInfo(taxpayerId);

      // Save expiration date to localStorage if available
      if (taxpayerInfo && taxpayerInfo["Business License"] && taxpayerInfo["Business License"]["Expiration Date"]) {
        const expirationDate = taxpayerInfo["Business License"]["Expiration Date"];
        const status = taxpayerInfo["Business License"]["Status"];
        localStorage.setItem("businessLicenseExpirationDate", expirationDate);
        localStorage.setItem("businessLicenseStatus", status);
      } else {
        localStorage.removeItem("businessLicenseExpirationDate");
        localStorage.removeItem("businessLicenseStatus");
      }

      // Save Type information if available
      if (taxpayerInfo && taxpayerInfo["Taxpayer Type"]) {
        const taxpayerType = taxpayerInfo["Taxpayer Type"];
        localStorage.setItem("taxpayerType", taxpayerType);
      } else {
        console.log("No taxpayer type found in taxpayer info");
        localStorage.removeItem("taxpayerType");
      }

      // Save Business License Type if available
      if (taxpayerInfo && taxpayerInfo["Business License"] && taxpayerInfo["Business License"]["Type"]) {
        const businessLicenseType = taxpayerInfo["Business License"]["Type"];
        localStorage.setItem("businessLicenseType", businessLicenseType);

        // Dispatch custom event for same-tab updates
        window.dispatchEvent(new Event('localStorageChange'));
      } else {
        console.log("No business license type found in taxpayer info");
        localStorage.removeItem("businessLicenseType");
        window.dispatchEvent(new Event('localStorageChange'));
      }

      // Save address information if available
      if (taxpayerInfo) {
        const addressParts = [];

        if (taxpayerInfo["Address"]) {
          addressParts.push(taxpayerInfo["Address"]);
        }
        if (taxpayerInfo["City"]) {
          addressParts.push(taxpayerInfo["City"]);
        }
        if (taxpayerInfo["State"]) {
          addressParts.push(taxpayerInfo["State"]);
        }
        if (taxpayerInfo["Zip"]) {
          addressParts.push(taxpayerInfo["Zip"]);
        }

        if (addressParts.length > 0) {
          const fullAddress = addressParts.join(", ");
          localStorage.setItem("businessAddress", fullAddress);
        } else {
          console.log("No address information found in taxpayer info");
          localStorage.removeItem("businessAddress");
        }
      }
    } catch (error) {
      console.error("Failed to fetch taxpayer information:", error);
      // Even if taxpayer info fetch fails, we still proceed with business selection
      localStorage.removeItem("businessLicenseExpirationDate");
      localStorage.removeItem("businessLicenseStatus");
      localStorage.removeItem("businessAddress");
      localStorage.removeItem("taxpayerType");
      localStorage.removeItem("businessLicenseType");
      window.dispatchEvent(new Event('localStorageChange'));
    } finally {
      setIsLoadingTaxPayerInfo(false);
      setShowBusinessSelector(false);
    }
  };

  // Handle Support Center click
  const handleSupportCenter = () => {
    console.log("appName", appName);
    if (appName === "My Tlingit & Haida Portal") {
      window.open("https://tlingitandhaida.gov/services/", "_blank");
    } else if (appName === "Colorado River Indian Tribes") {
      window.open("https://www.crit-nsn.gov/Revenue&Finance/", "_blank");
    }
  };

  // Handle Contact Us click
  const handleContactUs = () => {
    navigate("/contactus");
  };

  // Handle Portal Feature Card clicks for Tlingit & Haida
  const handlePortalFeatureClick = (feature) => {
    switch (feature) {
      case "View Your Account":
        navigate("/profile");
        break;
      case "Update Your Contact Info":
        navigate("/profile?option=Change Contact Info");
        break;
      case "Explore Services":
        
        break;
      case "Applications":
        
        break;
      case "Visit TlingitandHaida.gov":
        window.open("https://tlingitandhaida.gov", "_blank");
        break;
      default:
        break;
    }
  };

  const shouldShowPortalInfo = portalSegment !== "teroportal";

  // Portal Feature Cards for Tlingit & Haida
  const tlingitPortalFeatures = [
    {
      image: ccthImage1,
      text: "View Your Account",
    },
    {
      image: ccthImage2,
      text: "Update Your Contact Info",
    },
    {
      image: ccthImage3,
      text: "Explore Services",
    },
    {
      image: ccthImage4,
      text: "Applications",
    },
    {
      image: ccthImage5,
      text: "Visit TlingitandHaida.gov",
    },
  ];

  const portalFeatures = [
    {
      icon: <User className="w-6 h-6" />,
      title: "View Your Account Details",
      description: "Access and review your personal account information",
    },
    {
      icon: <Edit className="w-6 h-6" />,
      title: "Update Personal Information",
      description: "Modify and keep your details current",
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "Learn about various services",
      description: "Explore all available services and features",
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Access Application Forms",
      description: "Download and complete necessary forms",
    },
    {
      icon: <HelpCircle className="w-6 h-6" />,
      title: "Get Support & Contact Us",
      description: "Reach out for assistance and support",
    },
  ];

  // Modify portal features for Colorado River Indian Tribes
  const modifiedPortalFeatures = useMemo(() => {
    if (appName === "Colorado River Indian Tribes") {
      return portalFeatures.map((feature) => {
        const modified = { ...feature };
        
        // Change "personal" to "taxpayer" in first feature
        if (feature.title === "View Your Account Details") {
          modified.description = feature.description.replace("personal", "taxpayer");
        }
        
        // Change "Access Application Forms" to "Access Tax and Business License Forms"
        if (feature.title === "Access Application Forms") {
          modified.title = "Access Tax and Business License Forms";
        }
        
        // Change "Download and complete" to "Submit"
        if (feature.description === "Download and complete necessary forms") {
          modified.description = "Submit necessary forms";
        }
        
        // Change contact description
        if (feature.title === "Get Support & Contact Us") {
          modified.description = "taxinfo@crit-nsn.gov\nTel: (928) 575-1526\nFax: (928) 575-1540";
        }
        
        return modified;
      });
    }
    return portalFeatures;
  }, [appName]);

  // Check if current app is Pokagon Band of Potawatomi
  const isPbpApp = appName === "Prairie Band Potawatomi Nation";

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="fixed top-0 left-0 w-full z-50 bg-primary">
        <Header />
      </div>

      {/* Main Layout (Sidebar + Content) */}
      <div className="flex flex-1 mt-[72px]">
        {/* Sidebar */}
        <div className="fixed left-0 w-64 bg-white shadow-md h-[calc(100vh-72px)] overflow-y-auto hidden lg:block">
          <Sidebar />
        </div>

        <main className="flex-1 bg-gray-100 overflow-y-auto lg:ml-64 flex flex-col items-center">
          {/* Custom Business Selector Modal/Overlay */}
          {showBusinessSelector && (
            <div
              className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center"
              style={{ backdropFilter: "blur(2px)" }}
            >
              <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
                <h2 className="text-xl mb-4 font-bold text-gray-800 text-center">
                  Select Business for Profile
                </h2>
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-1">
                    Business Name
                  </label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    value={selectedBusinessIndex}
                    onChange={handleBusinessDropdownChange}
                    disabled={isLoadingTaxPayerInfo}
                  >
                    {businessList.map((biz, idx) => (
                      <option key={idx} value={idx}>
                        {biz["Business Name"] || `Business ${idx + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="w-full bg-primary text-white font-semibold rounded-lg py-2 px-4 mt-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleBusinessSelect}
                  disabled={isLoadingTaxPayerInfo}
                >
                  {isLoadingTaxPayerInfo ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Loading Business Info...
                    </>
                  ) : (
                    "Confirm Selection"
                  )}
                </button>
                
                {isLoadingTaxPayerInfo && (
                  <p className="text-sm text-gray-500 mt-3 text-center">
                    Fetching business details and license information...
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl overflow-hidden shadow-xl max-w-4xl mx-auto p-8 mt-10 w-full text-center">
            <div className="flex justify-center mb-8">
              <img src={appLogo} alt="CRIT Logo" className="w-32 h-auto" />
            </div>
            {/* Headings */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-1 bg-primary"></div>
              <h1 className="text-2xl font-bold text-gray-800">
                {ProgramTitle}
              </h1>
              <div className="w-12 h-1 bg-primary"></div>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome to the {ProgramTitle} Portal
            </h2>

            {/* Hide this h3 for Pokagon Band of Potawatomi */}
            {!isPbpApp && (
              <h3 className="text-2xl font-semibold text-gray-600 mb-4">
                {appName === "TERO" ? userType : appName === "Colorado River Indian Tribes" ? "Taxpayer Portal" : "Citizen Portal"}
              </h3>
            )}

            {/* Role-specific Lines */}
            {(Line1 || Line2) && (
              <div className="text-center p-6 rounded-lg">
                {Line1 && <p className="text-3xl font-bold text-gray-800 mb-2">{Line1}</p>}
                {Line2 && <p className="text-gray-600">{Line2}</p>}
              </div>
            )}

            {/* General Info - Hide for Pokagon Band of Potawatomi */}
            {shouldShowPortalInfo && !isPbpApp && (
              <>
                {appName !== "Colorado River Indian Tribes" && appName !== "My Tlingit & Haida Portal" && (
                  <p className="text-gray-500 max-w-lg mx-auto mb-8">
                    Your one-stop destination for member services and resources. Discover opportunities and connect with your community.
                  </p>
                )}

                {/* Portal Features Cards - Special layout for Tlingit & Haida Portal */}
                {appName === "My Tlingit & Haida Portal" ? (
                  <div className="flex flex-col items-center max-w-6xl mx-auto mb-8 w-full">
                    {/* Top row: 3 cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mb-6">
                      {tlingitPortalFeatures.slice(0, 3).map((feature, index) => (
                        <div
                          key={index}
                          onClick={() => handlePortalFeatureClick(feature.text)}
                          className="relative bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group"
                          style={{ aspectRatio: "4/3" }}
                        >
                          {/* Image - takes 70-80% of card */}
                          <div className="relative h-[75%] overflow-hidden bg-gray-200">
                            <img
                              src={feature.image}
                              alt={feature.text}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                // Hide broken image and show placeholder
                                e.target.style.display = "none";
                                const placeholder = document.createElement("div");
                                placeholder.className = "w-full h-full flex items-center justify-center bg-gray-200";
                                placeholder.innerHTML = `<div class="text-gray-500 text-center p-4">${feature.text}</div>`;
                                e.target.parentNode.appendChild(placeholder);
                              }}
                            />
                          </div>
                          {/* Black bar with white text - bottom 25% */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black h-[25%] flex items-center justify-center">
                            <h3 className="text-white font-semibold text-center px-4 text-sm md:text-base">
                              {feature.text}
                            </h3>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Bottom row: 2 cards centered */}
                    <div className="flex justify-center gap-6 w-full">
                      {tlingitPortalFeatures.slice(3, 5).map((feature, index) => (
                        <div
                          key={index + 3}
                          onClick={() => handlePortalFeatureClick(feature.text)}
                          className="relative bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group w-full md:w-[calc(33.333%-1rem)]"
                          style={{ aspectRatio: "4/3", maxWidth: "400px" }}
                        >
                          {/* Image - takes 70-80% of card */}
                          <div className="relative h-[75%] overflow-hidden bg-gray-200">
                            <img
                              src={feature.image}
                              alt={feature.text}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                // Hide broken image and show placeholder
                                e.target.style.display = "none";
                                const placeholder = document.createElement("div");
                                placeholder.className = "w-full h-full flex items-center justify-center bg-gray-200";
                                placeholder.innerHTML = `<div class="text-gray-500 text-center p-4">${feature.text}</div>`;
                                e.target.parentNode.appendChild(placeholder);
                              }}
                            />
                          </div>
                          {/* Black bar with white text - bottom 25% */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black h-[25%] flex items-center justify-center">
                            <h3 className="text-white font-semibold text-center px-4 text-sm md:text-base">
                              {feature.text}
                            </h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Regular Portal Features Cards for other apps */
                  <div className="flex flex-col items-center max-w-4xl mx-auto mb-8">
                    <p className="text-xl font-semibold text-gray-800 text-center mb-6">
                      In this portal, you can:
                    </p>
                    <div className="flex flex-wrap justify-center align-center gap-4">
                      {modifiedPortalFeatures
                        .filter(feature => 
                          appName !== "Colorado River Indian Tribes" || 
                          (feature.title !== "Update Personal Information" && feature.title !== "Learn about various services")
                        )
                        .map((feature, index) => (
                        <div
                          key={index}
                          className="w-60 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-100"
                        >
                          <div className="flex flex-col items-center mb-3">
                            <div className="text-primary mr-3 mb-2">{feature.icon}</div>
                            <h3 className="font-semibold text-gray-800 text-sm leading-tight">{feature.title}</h3>
                          </div>
                          {feature.title === "Get Support & Contact Us" && appName === "Colorado River Indian Tribes" ? (
                            <div className="text-gray-600 text-sm whitespace-pre-line text-center">
                              <a 
                                href="mailto:taxinfo@crit-nsn.gov" 
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                taxinfo@crit-nsn.gov
                              </a>
                              {"\nTel: "}
                              <a 
                                href="tel:+19285751526" 
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                (928) 575-1526
                              </a>
                              {"\nFax: (928) 575-1540"}
                            </div>
                          ) : (
                            <p className="text-gray-600 text-sm">{feature.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-10 flex items-center justify-center gap-6">
                  {appName !== "My Tlingit & Haida Portal" && (
                    <button
                      onClick={handleSupportCenter}
                      className={`px-6 py-3 bg-primary text-white rounded-lg transition-colors ${appName === "Pokagon Band of Potawatomi" || appName === "Colorado River Indian Tribes"
                        ? "hover:bg-background cursor-pointer"
                        : "cursor-default"
                        }`}
                    >
                      {appName === "Colorado River Indian Tribes" ? "Website" : "Support Center"}
                    </button>
                  )}
                  {appName === "My Tlingit & Haida Portal" && (
                    <button
                      onClick={handleContactUs}
                      className="px-6 py-3 bg-primary text-white rounded-lg transition-colors hover:bg-background cursor-pointer"
                    >
                      Contact Us
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Powered by TribeVue for Colorado River Indian Tribes */}
            {appName === "Colorado River Indian Tribes" && (
              <div className="text-center text-gray-500 text-sm mt-8 mb-4">
                Powered by TribeVue®
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Home;